"use client";

import { useCallback, useEffect } from "react";

declare global {
  interface Window {
    grecaptcha?: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

let recaptchaScriptLoadingPromise: Promise<void> | null = null;

function loadRecaptchaScript(siteKey: string): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.grecaptcha) return Promise.resolve();

  if (recaptchaScriptLoadingPromise) {
    return recaptchaScriptLoadingPromise;
  }

  const existingScript = document.querySelector<HTMLScriptElement>(
    'script[src*="recaptcha/api.js"]'
  );

  if (existingScript) {
    recaptchaScriptLoadingPromise = new Promise((resolve) => {
      if (window.grecaptcha) {
        resolve();
      } else {
        existingScript.addEventListener("load", () => resolve(), { once: true });
        existingScript.addEventListener("error", () => resolve(), { once: true });
      }
    });
    return recaptchaScriptLoadingPromise;
  }

  recaptchaScriptLoadingPromise = new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => resolve();
    document.head.appendChild(script);
  });

  return recaptchaScriptLoadingPromise;
}

/**
 * Hook to execute Google reCAPTCHA v3 on-demand.
 * Loads the external script only when requested or upon form interaction,
 * preventing third-party cookies and script execution on initial page load.
 */
export function useRecaptcha() {
  const siteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY?.trim();

  // Lazy-load reCAPTCHA on user interaction (preserves 100% Lighthouse score on initial load)
  const loadRecaptcha = useCallback(() => {
    if (siteKey) {
      loadRecaptchaScript(siteKey).then(() => {
        const badge = document.querySelector<HTMLElement>(".grecaptcha-badge");
        if (badge) {
          badge.style.display = "block";
        }
      });
    }
  }, [siteKey]);

  // Hide badge if user navigates away to a non-form page
  useEffect(() => {
    return () => {
      const badge = document.querySelector<HTMLElement>(".grecaptcha-badge");
      if (badge) {
        badge.style.display = "none";
      }
    };
  }, []);

  const executeRecaptcha = useCallback(
    async (action: string = "submit"): Promise<string | null> => {
      if (!siteKey || typeof window === "undefined") {
        return null;
      }

      await loadRecaptchaScript(siteKey);

      // If window.grecaptcha is not yet ready, wait briefly up to 3s
      if (!window.grecaptcha) {
        const start = Date.now();
        while (!window.grecaptcha && Date.now() - start < 3000) {
          await new Promise((resolve) => setTimeout(resolve, 100));
        }
      }

      if (!window.grecaptcha) {
        console.warn("[reCAPTCHA] window.grecaptcha is unavailable.");
        return null;
      }

      return new Promise<string | null>((resolve) => {
        try {
          window.grecaptcha!.ready(async () => {
            try {
              const token = await window.grecaptcha!.execute(siteKey, { action });
              resolve(token);
            } catch (err) {
              console.error("[reCAPTCHA] Execution failed:", err);
              resolve(null);
            }
          });
        } catch (err) {
          console.error("[reCAPTCHA] Exception invoking execute:", err);
          resolve(null);
        }
      });
    },
    [siteKey]
  );

  return {
    executeRecaptcha,
    loadRecaptcha,
    siteKey,
  };
}
